import { useEffect, useState } from 'react';
import { api, useApi, useMutation } from '../../api';
import type { MeView } from '../../api';
import { LoadingState, ErrorState, TwoFactorCard, PasswordCard } from '../../components';
import { useT, useRoleLabel } from '../../i18n';
import { useOffices } from '../../offices';
import dict from './Settings.i18n';

/**
 * Agent · Settings — self-service profile + security for the logged-in principal.
 * Route /agent/settings (any authenticated staff member; the page acts only on the
 * caller's own account). Variant-B clean design; the AgentLayout → PortalLayout shell
 * supplies the `.cl` sidebar + the `col grow` content container, so this screen renders
 * only the topbar + a single scrollable column of `.panel` cards.
 *
 * Wired to the live self-service API (no invented endpoints):
 *   - GET   /auth/me            → api.auth.me()            (read the current principal)
 *   - PATCH /me                 → api.me.update            (own name / phone; office is manager-only)
 *   - POST  /auth/change-password → api.auth.changePassword (verifies current pw,
 *                                   revokes other sessions server-side)
 *
 * 2FA is mandatory and enrolled at first login; there is NO self-service reset for the
 * agent (only admins reset others via /users/:id/reset-2fa), so the 2FA card is
 * status-only with a "contact an administrator" note.
 */

/**
 * Seed the firstName/lastName inputs. Prefer the discrete columns; if both are
 * empty but a fullName is present, split fullName on the first space so the
 * fields still show something useful (everything after the first space = last).
 */
function seedFirstName(me: MeView): string {
  if (me.firstName) return me.firstName;
  if (me.lastName) return '';
  return (me.fullName ?? '').split(' ')[0] ?? '';
}
function seedLastName(me: MeView): string {
  if (me.lastName) return me.lastName;
  if (me.firstName) return '';
  const full = me.fullName ?? '';
  const i = full.indexOf(' ');
  return i === -1 ? '' : full.slice(i + 1);
}

export default function AgentSettings() {
  const t = useT(dict);
  const { data: me, loading, error, reload } = useApi(() => api.auth.me(), []);

  return (
    <>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{t('topbarMeta')}</div>
          <div className="h1">{t('topbarTitle')}</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 22px', overflow: 'auto', flex: 1 }}>
        {loading ? (
          <LoadingState label={t('loadingProfile')} />
        ) : error ? (
          <ErrorState message={error.message} onRetry={reload} />
        ) : me ? (
          <div className="col gap-16" style={{ maxWidth: 640 }}>
            <ProfileCard me={me} onSaved={reload} />
            <PasswordCard />
            <TwoFactorCard me={me} onChanged={reload} />
          </div>
        ) : null}
      </div>
    </>
  );
}

// ── 1. Profile ───────────────────────────────────────────────────────────────
function ProfileCard({ me, onSaved }: { me: MeView; onSaved: () => void }) {
  const t = useT(dict);
  const roleLabel = useRoleLabel();
  const { officeName } = useOffices();
  const update = useMutation(api.me.update);
  const [firstName, setFirstName] = useState(() => seedFirstName(me));
  const [lastName, setLastName] = useState(() => seedLastName(me));
  const [phone, setPhone] = useState(me.phone ?? '');
  const [saved, setSaved] = useState(false);

  // Re-seed the form if the underlying profile reloads.
  useEffect(() => {
    setFirstName(seedFirstName(me));
    setLastName(seedLastName(me));
    setPhone(me.phone ?? '');
  }, [me]);

  // Canonical display name kept in sync with the two name parts.
  const fullName = [firstName, lastName].map((s) => s.trim()).filter(Boolean).join(' ');

  const dirty =
    firstName.trim() !== (seedFirstName(me)).trim() ||
    lastName.trim() !== (seedLastName(me)).trim() ||
    phone.trim() !== (me.phone ?? '');
  const canSave = dirty && fullName.length > 0 && !update.loading;

  async function save() {
    setSaved(false);
    try {
      await update.mutate({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        fullName,
      });
      setSaved(true);
      onSaved();
    } catch {
      /* update.error surfaced below */
    }
  }

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h3">{t('profileHeading')}</div>
      </div>
      <div className="col gap-12" style={{ padding: 16 }}>
        <div className="row gap-12">
          <label className="col gap-4 grow">
            <span className="label">{t('emailLabel')}</span>
            <div className="input" style={{ background: 'var(--paper)' }}>
              <span className="mono" style={{ fontSize: 13 }}>{me.email}</span>
            </div>
          </label>
          <label className="col gap-4" style={{ width: 180 }}>
            <span className="label">{t('roleLabel')}</span>
            <div className="input" style={{ background: 'var(--paper)' }}>
              <span style={{ fontSize: 14 }}>{roleLabel(me.role)}</span>
            </div>
          </label>
        </div>
        <div className="meta" style={{ fontSize: 12 }}>
          {t('emailRoleNotice')}
        </div>

        <div className="row gap-12">
          <label className="col gap-4 grow">
            <span className="label">{t('firstNameLabel')}</span>
            <input
              className="input"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setSaved(false);
              }}
              placeholder={t('firstNamePlaceholder')}
            />
          </label>
          <label className="col gap-4 grow">
            <span className="label">{t('lastNameLabel')}</span>
            <input
              className="input"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setSaved(false);
              }}
              placeholder={t('lastNamePlaceholder')}
            />
          </label>
        </div>
        <label className="col gap-4">
          <span className="label">{t('phoneLabel')}</span>
          <input
            className="input"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setSaved(false);
            }}
            placeholder={t('phonePlaceholder')}
          />
        </label>
        <label className="col gap-4">
          <span className="label">{t('officeLabel')}</span>
          <input className="input" value={officeName(me.office) || '—'} readOnly disabled />
          <span className="meta" style={{ fontSize: 12 }}>{t('officeManagedHint')}</span>
        </label>

        {update.error && <ErrorState message={update.error.message} />}
        {saved && !dirty && (
          <div className="meta" style={{ color: 'var(--green)', fontSize: 13 }}>
            {t('profileSaved')}
          </div>
        )}

        <div className="row end center">
          <button type="button" className="btn primary" disabled={!canSave} onClick={save}>
            {update.loading ? t('savingBtn') : t('saveChangesBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}


